import Dexie, { type Table } from 'dexie';
import { isBrowserCloudHost } from '@application/licence/CloudWebHost';
import { resetCloudBrowserCaches } from '@infra/persistence/cloudBrowserReset';
import { logger } from '@infra/telemetry/Logger';
import type { Tenant } from '@shared/types/Tenant';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import type { Facture } from '@shared/types/Facture';
import type { CentreAnalytique } from '@shared/types/CentreAnalytique';
import type { AuditLogEntry } from '@shared/types/AuditLog';
import type { User } from '@shared/types/User';

/**
 * Détecte un conflit de schéma IndexedDB (VersionError / libellé FR navigateur).
 */
export function isIndexedDbVersionConflict(err: unknown): boolean {
  const name =
    err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : '';
  const msg = err instanceof Error ? err.message : String(err);
  return (
    name === 'VersionError' ||
    /VersionError/i.test(msg) ||
    /conflit de version/i.test(msg) ||
    /requested version is (less|lower)/i.test(msg)
  );
}

/**
 * Base de donnees IndexedDB de Nexxus ERP.
 */
export class NexxusDatabase extends Dexie {
  tenants!: Table<Tenant, string>;
  salaries!: Table<Salarie, string>;
  bulletins!: Table<Bulletin, string>;
  ecritures!: Table<Ecriture, string>;
  factures!: Table<Facture, string>;
  centres_analytiques!: Table<CentreAnalytique, string>;
  audit_log!: Table<AuditLogEntry, string>;
  users!: Table<User, string>;

  private static _instance: NexxusDatabase | null = null;

  constructor(name = 'NexxusERP') {
    super(name);
    this.version(1).stores({
      tenants: 'id, nom, nif',
      salaries: 'id, tenant_id, matricule, statut, statut_contrat, [tenant_id+statut]',
      bulletins: 'id, tenant_id, salarie_id, periode, [tenant_id+periode], [salarie_id+periode]',
      ecritures:
        'id, tenant_id, periode, journal, compte, source, [tenant_id+periode], [tenant_id+compte], [tenant_id+axe_cdc]',
      factures: 'id, tenant_id, type, numero, date, tiers_id, [tenant_id+type]',
      centres_analytiques: 'id, tenant_id, code, type, [tenant_id+actif]',
      audit_log: 'id, tenant_id, date, utilisateur, action, [tenant_id+date]',
      users: 'id, tenant_id, email, role',
    });
  }

  static getInstance(): NexxusDatabase {
    if (!this._instance) this._instance = new NexxusDatabase();
    return this._instance;
  }

  /**
   * Cloud : une ancienne visite recette peut laisser une IndexedDB plus récente
   * que le schéma actuel → « Conflit de version ». Le dossier métier est chez
   * Nexxus : on peut vider ce cache navigateur et rouvrir.
   */
  async openWithRecovery(): Promise<void> {
    try {
      await this.open();
    } catch (err) {
      if (!isBrowserCloudHost()) throw err;
      logger.warn({
        event: 'indexeddb_cloud_reset',
        conflict: isIndexedDbVersionConflict(err),
        error: err instanceof Error ? err.message : String(err),
      });
      this.close();
      await resetCloudBrowserCaches();
      await Dexie.delete(this.name);
      await this.open();
    }
  }
}

export function getDatabase(): NexxusDatabase {
  return NexxusDatabase.getInstance();
}
